import { Link } from "react-router";
import { aguardaClienteNoModulo, contatoDoModulo, nomeDaEtapaDoModulo } from "../../nucleo/andamentoDoModulo";
import type { Esteira } from "../../nucleo/implantacoes";
import { Cartao, Chip } from "../ui";

const TEXTO_DA_ETAPA = {
  "Integração": { texto: "Nossa equipe de integração está conectando o GEOvendas ao seu ERP e fazendo as validações iniciais. Logo entraremos em contato para que as validações sejam feitas junto com você.", proxima: "Treinamento" },
  "Aguardando você": { texto: "Precisamos de uma informação ou ação sua para continuar.", proxima: null },
  "Treinamento": { texto: "Nossa consultora vai agendar o treinamento com o key user deste módulo. A data e o link aparecem na Agenda.", proxima: "Acompanhamento" },
  "Acompanhamento": { texto: "O treinamento foi realizado. Agora acompanhamos os primeiros dias de uso e ajustamos o que for preciso.", proxima: "Concluído" },
  "Concluído": { texto: "Módulo entregue. A partir de agora, o atendimento deste módulo é feito pela equipe de Atendimento, pelo menu Chamados.", proxima: null },
} as const;

/** Cada status do Jira no resumo, na ordem em que o módulo anda. */
const NO_RESUMO: ReadonlyArray<readonly [string, (total: number) => string]> = [
  ["Integration", (total) => `${total} em integração`],
  ["In Progress", (total) => `${total} em progresso`],
  ["Training", (total) => `${total} em treinamento`],
  ["Follow Up", (total) => `${total} em acompanhamento`],
  ["Done", (total) => `${total} ${total === 1 ? "concluído" : "concluídos"}`],
];

export function resumoDoAndamentoDosModulos(esteira: Esteira): string | null {
  if (!esteira.acompanhamento_liberado) return null;
  // Sem nenhum status vindo do Jira, não há andamento para resumir: quem exibe diz que aguarda.
  if (!esteira.modulos.some((modulo) => modulo.jira_status_nome)) return null;
  const emAndamento = esteira.modulos.filter((modulo) => !aguardaClienteNoModulo(modulo));
  const aguardando = esteira.modulos.length - emAndamento.length;
  const conhecidos = new Set(NO_RESUMO.map(([status]) => status));
  const semEtapa = emAndamento.filter((modulo) => !conhecidos.has(modulo.jira_status_nome ?? "")).length;
  const partes = [
    aguardando ? `${aguardando} ${aguardando === 1 ? "módulo aguardando você" : "módulos aguardando você"}` : null,
    ...NO_RESUMO.map(([status, frase]) => {
      const total = emAndamento.filter((modulo) => modulo.jira_status_nome === status).length;
      return total ? frase(total) : null;
    }),
    semEtapa ? `${semEtapa} aguardando atualização` : null,
  ].filter(Boolean);
  return partes.join(" · ");
}

/** Depois do aceite, cada tarefa do Jira anda por conta própria. */
export function AndamentoDosModulos({ esteira }: { esteira: Esteira }) {
  if (!esteira.acompanhamento_liberado) return null;

  const resumo = resumoDoAndamentoDosModulos(esteira) ?? "Aguardando atualização dos módulos";

  return (
    <section className="mb-6" aria-labelledby="titulo-execucao-por-modulo" data-testid="andamento-modulos">
      <h4 className="titulo-secao" id="titulo-execucao-por-modulo">Andamento dos módulos</h4>
      <p className="lead" data-testid="resumo-dos-modulos">{resumo}</p>
      <div className="grid gap-3 md:grid-cols-2">
        {esteira.modulos.map((modulo) => {
          const etapa = nomeDaEtapaDoModulo(modulo.jira_status_nome) ?? "Aguardando atualização";
          const aguardando = aguardaClienteNoModulo(modulo);
          const contato = contatoDoModulo(modulo, esteira.contato);
          const conteudo = aguardando ? TEXTO_DA_ETAPA["Aguardando você"] : TEXTO_DA_ETAPA[etapa as keyof typeof TEXTO_DA_ETAPA];
          return (
            <Cartao key={modulo.chave} titulo={modulo.nome} data-testid={`andamento-${modulo.chave}`}
              acessorio={<Chip estado={aguardando ? "atencao" : "neutro"}>{aguardando ? "Aguardando você" : etapa}</Chip>}>
              <details className="modulo__detalhe">
                <summary>Ver o que acontece nesta etapa</summary>
                <p className="cartao__texto"><b>O que acontece aqui:</b> {conteudo?.texto ?? "A equipe atualizará o andamento deste módulo."}</p>
                {conteudo?.proxima ? <p className="cartao__texto"><b>Próxima etapa:</b> {conteudo.proxima}</p> : null}
                <p className="cartao__texto">Seu contato: {contato?.nome ?? "a definir pela GEOvendas"}</p>
                {contato?.email ? <p className="cartao__texto"><a href={`mailto:${contato.email}`}>{contato.email}</a></p> : null}
                {contato && "telefone" in contato && contato.telefone ? <p className="cartao__texto"><a href={`tel:${String(contato.telefone).replace(/[^+\d]/g, "")}`}>{contato.telefone}</a></p> : null}
                <Link className="botao botao--fantasma mt-3" to={`/app/modulos/${encodeURIComponent(modulo.chave)}`}>
                  Ver módulo
                </Link>
              </details>
            </Cartao>
          );
        })}
      </div>
    </section>
  );
}
